import { Facebook, Twitter, Linkedin, MessageCircle, Send, Mail, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function getRef(user) {
  if (user?.id) return { ref: user.id, name: `${user.first_name || ""} ${user.last_name || ""}`.trim(), email: user.email || "" };
  let ref = localStorage.getItem("aca_ref");
  if (!ref) {
    ref = "anon-" + Math.random().toString(36).slice(2, 10);
    localStorage.setItem("aca_ref", ref);
  }
  return { ref, name: "", email: "" };
}

export const ShareBar = ({ url, title = "", publicationId, compact = false, hashtags = [] }) => {
  const { user } = useAuth();
  const { ref, name, email } = getRef(user);
  // Lien de partage servi par le serveur (aperçu riche Open Graph sur les réseaux sociaux).
  const shareUrl = publicationId
    ? `${BACKEND}/api/share/${publicationId}?ref=${encodeURIComponent(ref)}`
    : url + (url.includes("?") ? "&" : "?") + "ref=" + encodeURIComponent(ref);
  const u = encodeURIComponent(shareUrl);
  const t = encodeURIComponent(title);
  // Hashtags de l'article inclus dans le partage X (meilleure visibilité sociale).
  const xTags = (hashtags || []).map((h) => String(h).replace(/^#/, "").replace(/\s+/g, "")).filter(Boolean).slice(0, 3).join(",");
  const xHref = `https://twitter.com/intent/tweet?url=${u}&text=${t}${xTags ? `&hashtags=${xTags}` : ""}`;

  const record = (network) => {
    if (!publicationId) return;
    api.post("/public/blog/share", { publication_id: publicationId, ref, network, name, email }).catch(() => {});
  };

  const links = [
    { key: "facebook", label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
    { key: "x", label: "X", icon: Twitter, href: xHref },
    { key: "linkedin", label: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${t}%20${u}` },
    { key: "telegram", label: "Telegram", icon: Send, href: `https://t.me/share/url?url=${u}&text=${t}` },
    { key: "email", label: "E-mail", icon: Mail, href: `mailto:?subject=${t}&body=${u}` },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      record("copie");
      toast.success("Lien copié.");
    } catch {
      toast.error("Impossible de copier le lien.");
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="share-bar">
      {!compact && <span className="mr-1 text-xs uppercase tracking-[0.2em] text-ink-tertiary">Partager</span>}
      {links.map((l) => (
        <a
          key={l.key}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          title={l.label}
          onClick={() => record(l.key)}
          data-testid={`share-${l.key}`}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-ink-secondary transition-all hover:-translate-y-0.5 hover:border-cyan hover:text-cyan"
        >
          <l.icon size={16} />
        </a>
      ))}
      <button
        onClick={copy}
        title="Copier le lien"
        data-testid="share-copy"
        className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-ink-secondary transition-all hover:-translate-y-0.5 hover:border-cyan hover:text-cyan"
      >
        <Link2 size={16} />
      </button>
    </div>
  );
};
